import type { ReactNode } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { KpiMetricCard } from '@/components/shared/KpiMetricCard'
import {
  useRegulatoryKpis,
  type RegulatoryFilters,
  type RegulatoryTimeframe,
} from '@/hooks/useRegulatoryQueries'

function formatEur(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '€0'
  if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `€${(n / 1_000).toFixed(0)}K`
  return `€${Math.round(n)}`
}

/**
 * P4f (audit item 22): the user-visible "(90D)" labels were hardcoded
 * even when the active timeframe was something else. The KPI rail now
 * derives a window suffix from either the filter prop or the response
 * `window` echo.
 *
 *   30d  → "Last 30D"
 *   90d  → "Last 90D"
 *   365d → "Last 365D"
 *   all  → "All time"
 */
function timeframeSuffix(tf: RegulatoryTimeframe): string {
  if (tf === 'all') return 'All time'
  return `Last ${tf.toUpperCase()}`
}

interface Props {
  /**
   * Slot for future KPI cards (Stage 2 adds "Frameworks tracked").
   */
  extraCardsSlot?: ReactNode
  /**
   * Page-level filter state. Threaded into the underlying query so the rail
   * refetches when the user toggles the timeframe / jurisdiction. Optional
   * for backwards compatibility with any caller that hasn't adopted the
   * Wave-1 filter rail yet — when omitted the server defaults apply.
   */
  filters?: Pick<RegulatoryFilters, 'jurisdiction' | 'timeframe'>
}

export function RegulatoryKpiRail({ extraCardsSlot, filters }: Props) {
  const { data, isLoading, isError } = useRegulatoryKpis(filters)

  if (isLoading) {
    return (
      <div
        data-testid="kpi-rail"
        className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-4"
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div
        data-testid="kpi-rail-error"
        role="alert"
        className="text-sm text-destructive"
      >
        Unable to load KPIs.
      </div>
    )
  }

  // Prefer the response `window` echo (server is the source of truth for
  // which window actually drove the aggregate). Fall back to the prop, then
  // to the legacy "(90d)" copy if neither is present (older API responses).
  const activeWindow: RegulatoryTimeframe =
    data.window ?? filters?.timeframe ?? '90d'
  const suffix = timeframeSuffix(activeWindow)
  const fineVolume =
    typeof data.fineVolumeWindow === 'number'
      ? data.fineVolumeWindow
      : data.fineVolume90d
  const actions =
    typeof data.actionsWindow === 'number'
      ? data.actionsWindow
      : data.actions90d

  return (
    <div
      data-testid="kpi-rail"
      data-window={activeWindow}
      className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-4"
    >
      <KpiMetricCard label="Active regulators" value={data.activeRegulators} />
      <KpiMetricCard
        label={`Fine volume (${suffix})`}
        value={formatEur(fineVolume)}
      />
      <KpiMetricCard label={`Actions (${suffix})`} value={actions} />
      {extraCardsSlot}
    </div>
  )
}
